import { useCallback, useEffect, useRef, useState } from 'react';

import { useGeolocationState } from '@/contexts/GeolocationContext';

/**
 * Owns a City/Zip input's value and keeps it in sync with the patient's saved
 * geolocation when that location loads after mount.
 *
 * Seeds with `initialFromUrl || recentSearch || ''`. If the saved location is
 * still loading on first render, an effect fills the field from `recentSearch`
 * once it arrives — but only if the patient has not already edited the field
 * and `initialFromUrl` is empty, so the patient's own input always wins.
 *
 * @param {string} [initialFromUrl=''] - a zip or city already in the page address.
 * @returns {[string, (next: string) => void]} the current value and a setter
 * that marks the field as patient-edited.
 */
export const useGeolocationInputValue = (initialFromUrl = '') => {
    const geolocationState = useGeolocationState();

    const [value, setInternalValue] = useState(
        initialFromUrl || geolocationState?.recentSearch || '',
    );

    const editedRef = useRef(false);

    const setValue = useCallback((next) => {
        editedRef.current = true;
        setInternalValue(next);
    }, []);

    useEffect(() => {
        if (editedRef.current || initialFromUrl) {
            return;
        }

        if (geolocationState?.recentSearch) {
            setInternalValue(geolocationState.recentSearch);
        }
        // initialFromUrl is intentionally omitted: it's stable for this component's
        // lifetime, and only a fresh recentSearch should trigger the sync.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geolocationState?.recentSearch]);

    return [value, setValue];
};
